<template>
  <v-container grid-list-md>
    <v-row>
      <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2">
        <v-card v-if="showHeader || showImage || description">
          <v-toolbar v-if="showHeader">
            <v-toolbar-title>{{ name }}</v-toolbar-title>
          </v-toolbar>
          <v-img v-if="showImage" :src="`/img/theatre/${showId}.jpg`" height="300">
          </v-img>
          <v-card-text v-if="description" class="theatre-event-description" v-html="description" />
        </v-card>
        <v-card v-if="purchaseComplete">
          <v-card-title>Purchase Complete</v-card-title>
          <v-card-text>
            <p>Thank you for your purchase. </p>
          </v-card-text>
        </v-card>
        <template v-else>
          <v-card class="mt-2">
            <v-card-text>
              <h2>Performances</h2>
              <p>Please choose a performance below to purchase your tickets for that performance.</p>
              <v-list>
                <!-- <purchase-dialog
                  v-for="perf of performances"
                  :key="'perf-' + perf._id"
                  :show-id="showId"
                  :name="name"
                  :performance="perf" /> -->
                <purchase-expander
                  v-for="perf of performances"
                  :key="'perf-' + perf._id"
                  :show-id="showId"
                  :name="name"
                  :performance="perf"
                  :is-admin="isAdmin"
                  @update="(upd) => updateTickets(perf, upd)" />
              </v-list>
            </v-card-text>
          </v-card>
          <v-card v-if="tickets.length > 0">
            <v-card-text>
              <p v-if="isLoggedIn">Since you are logged in, this purchase will be linked to your Covenant account:<br/>Name: {{ user.preferred }} {{ user.last }}<br/>Email: {{ user.email }}</p>
              <v-row v-else>
                <v-col cols="12" class="pb-0">
                  <p>Please complete this form to purchase your tickets.</p>
                  <p>You are currently not logged in. If you are an employee or student and want to see these options, please log in.</p>
                </v-col>
                <v-col>
                  <v-text-field v-model="first" label="First Name" outlined hide-details />
                </v-col>
                <v-col>
                  <v-text-field v-model="last" label="Last Name" outlined hide-details />
                </v-col>
                <v-col cols="12">
                  <v-text-field v-model="email" label="Email Address" outlined />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-btn :disabled="!(isLoggedIn || (first && last && email))" @click="saveTickets()">{{ paymentTotal > 0 ? 'Pay with PayPal' : 'Submit Reservation' }}</v-btn>
            </v-card-actions>
            <payment-popup ref="paymentPopup" :total="paymentTotal" :items="paymentItems" :show-activator="false" department="Theatre" :foapal="{ fund: '9999', org: '9999', acct: '99999', prog: '99' }" @success="paymentSuccessful" @cancel="cancelTickets" />
          </v-card>
        </template>
      </v-col>
    </v-row>
  </v-container>
</template>
<style>
div.theatre-event-description h1, div.theatre-event-description h2, div.theatre-event-description h3 {
  margin-bottom: .3em;
}
</style>
<script>
import { ref, computed, onMounted } from '@vue/composition-api'

export default {
  components: {
    PaymentPopup: () => import('@/components/core/PaymentPopup'),
    PurchaseExpander: () => import('@/components/theatre/purchaseExpander')
    // PurchaseDialog: () => import('@/components/theatre/purchaseDialog')
  },
  setup (props, { root }) {
    const user = computed(() => 'spoof' in root.$store.state.user ? root.$store.state.user.spoof : root.$store.state.user)
    const isLoggedIn = computed(() => root.$store.state.loggedIn)
    const isAdmin = computed(() => isLoggedIn.value && user.value.roles.includes('Technology Services'))
    const showId = ref('')
    const shortName = computed(() => root.$route.params.show)
    const showHeader = ref(true)
    const name = ref('')
    const showImage = ref(false)
    const description = ref('')
    const performances = ref([])
    const discount = ref({
      available: false,
      limit: 0,
      price: 0,
      used: true
    })

    const isLoaded = ref(false)

    onMounted(async () => {
      let query = {
        active: true,
        shortName: shortName.value,
        $limit: 1
      }
      const { data } = await root.$feathers.service('theatre/show').find({ query })
      if (data.length === 0) {
        root.$dispatch('main/snackbar', { color: 'error', text: 'The show you attempted to view was not found' })
        root.$router.replace('/theatre')
        return
      }
      showId.value = data[0]._id
      showHeader.value = data[0].showHeader
      name.value = data[0].name
      showImage.value = data[0].image == null || data[0].image
      description.value = data[0].description
      const parser = new DOMParser()
      const doc = parser.parseFromString(data[0].description, 'text/html')
      description.value = ((doc.body.textContent || '') === '') ? '' : data[0].description
      performances.value = data[0].performances.sort((a, b) => new Date(a.date) < new Date(b.date) ? -1 : 1).map((row) => {
        const dt = new Date(row.date)
        const date = dt.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' })
        const time = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })
        row.dateStr = `${date} at ${time}`
        return row
      })
      isLoaded.value = true
    })

    const tickets = ref([])
    const first = ref('')
    const last = ref('')
    const email = ref('')
    function updateTickets (performance, { label, count }) {
      const index = tickets.value.findIndex(({ performance: perfId, label: l }) => perfId === performance._id && label === l)
      if (index >= 0) {
        tickets.value.splice(index, 1, { ...tickets.value[index], tickets: count })
      } else {
        const { price } = performance.prices.find(({ label: l }) => label === l)
        tickets.value.push({ performance: performance._id, date: performance.date, label, tickets: count, price, total: price * count })
      }
    }
    const paymentTotal = computed(() => tickets.value.reduce((prev, { total }) => prev + total, 0))

    const paymentPopup = ref(null)
    const paymentItems = computed(() => tickets.value.map(({ date, label, tickets, price }) => {
      return { name: `${name.value} on ${date}: ${label}`, quantity: tickets, unit_amount: { currency_code: 'USD', value: price } }
    }))
    const purchaseComplete = ref(false)
    const purchaseDetails = ref({
      paypalId: '',
      payerName: '',
      payerEmail: '',
      amount: '',
      items: []
    })
    async function paymentSuccessful ({ logId, paypalId }) {
      console.log('Payment was successful')
      // Update the saved tickets record to show that it's been paid
      if (ticketId.value) await root.$feathers.service('theatre/ticket').patch(ticketId.value, { status: 'Paid' })
      // Load the payment information so we can tell them that the payment was successful
      purchaseDetails.value = await root.$feathers.service('system/paypal-log').get(logId)
    }

    const ticketId = ref(null)
    async function saveTickets () {
      // Save the tickets as a pending record, just in case the payment gets disconnected.
      if (ticketId.value) {
        await root.$feathers.service('theatre/ticket').patch(ticketId.value, {
          first: isLoggedIn.value ? user.value.first : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          tickets: tickets.value,
          status: 'Reserved'
        })
      } else {
        const res = await root.$feathers.service('theatre/ticket').create({
          first: isLoggedIn.value ? user.value.preferred : first.value,
          last: isLoggedIn.value ? user.value.last : last.value,
          email: isLoggedIn.value ? user.value.email : email.value,
          bannerId: isLoggedIn.value ? user.value.bannerId : null,
          show: showId.value,
          tickets: tickets.value
        })
        if (res) ticketId.value = res._id
      }
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (performance in idHash) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] += tickets
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
      // We can use a script to load payment records from PayPal and find ones that aren't in the log
      // and match them up to these saved records and mark them as paid.
      paymentPopup.value.activate()
    }

    async function cancelTickets () {
      await root.$feathers.service('theatre/ticket').patch(ticketId.value, { status: 'Cancelled' })
      const $inc = {}
      let index = 0
      const arrayFilters = []
      const idHash = {}
      tickets.value.forEach(({ performance, tickets }) => {
        if (idHash[performance]) {
          $inc[`performances.$[perf${idHash[performance]}].reserved`] = tickets * -1
        } else {
          $inc[`performances.$[perf${index}].reserved`] = tickets * -1
          arrayFilters.push({ [`perf${index}._id`]: performance })
          idHash[performance] = index
          index++
        }
      })
      await root.$feathers.service('theatre/show').patch(showId.value, { $inc }, { query: { arrayFilters } })
    }

    return {
      user,
      isLoggedIn,
      isAdmin,
      showId,
      shortName,
      showHeader,
      name,
      showImage,
      description,
      performances,
      discount,
      tickets,
      first,
      last,
      email,
      updateTickets,
      paymentTotal,
      paymentPopup,
      paymentItems,
      purchaseComplete,
      purchaseDetails,
      paymentSuccessful,
      saveTickets,
      cancelTickets
    }
  }
}
</script>
